import { useState, useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { fetchContactInfo, submitContactForm, resolveAssetPath, type ContactInfo } from "../../api";

interface ChatChannel {
  id: string;
  name: string;
  icon: string;
  qrCode: string;
  label: string;
  color: string;
}

const defaultChannels: ChatChannel[] = [
  {
    id: "wechat",
    name: "WeChat",
    icon: "ri-wechat-line",
    qrCode: "https://readdy.ai/api/search-image?query=WeChat%20QR%20code%20placeholder%20for%20customer%20contact%20scan%20me%20digital%20code%20square%20pattern%20on%20clean%20white%20background%20modern%20minimalist%20style&width=400&height=400&seq=qr1&orientation=squarish",
    label: "Scan to add us on WeChat",
    color: "#07C160",
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    icon: "ri-whatsapp-line",
    qrCode: "https://readdy.ai/api/search-image?query=WhatsApp%20QR%20code%20placeholder%20for%20customer%20contact%20scan%20me%20digital%20code%20square%20pattern%20on%20clean%20white%20background%20modern%20minimalist%20style&width=400&height=400&seq=qr2&orientation=squarish",
    label: "Scan to chat on WhatsApp",
    color: "#25D366",
  },
  {
    id: "telegram",
    name: "Telegram",
    icon: "ri-telegram-line",
    qrCode: "https://readdy.ai/api/search-image?query=Telegram%20QR%20code%20placeholder%20for%20customer%20contact%20scan%20me%20digital%20code%20square%20pattern%20on%20clean%20white%20background%20modern%20minimalist%20style&width=400&height=400&seq=qr3&orientation=squarish",
    label: "Scan to chat on Telegram",
    color: "#0088CC",
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeQr, setActiveQr] = useState<ChatChannel | null>(null);
  const [info, setInfo] = useState<ContactInfo | null>(null);
  const [chatChannels, setChatChannels] = useState<ChatChannel[]>(defaultChannels);

  useEffect(() => {
    fetchContactInfo()
      .then((data) => {
        setInfo(data);
        // 如果有实际二维码图片，更新聊天渠道
        if (data.wechat_qr || data.whatsapp_qr || data.telegram_qr) {
          setChatChannels((prev) =>
            prev.map((ch) => {
              if (ch.id === "wechat" && data.wechat_qr) return { ...ch, qrCode: resolveAssetPath(data.wechat_qr) || data.wechat_qr };
              if (ch.id === "whatsapp" && data.whatsapp_qr) return { ...ch, qrCode: resolveAssetPath(data.whatsapp_qr) || data.whatsapp_qr };
              if (ch.id === "telegram" && data.telegram_qr) return { ...ch, qrCode: resolveAssetPath(data.telegram_qr) || data.telegram_qr };
              return ch;
            })
          );
        }
      })
      .catch((err) => console.warn("联系信息加载失败:", err));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContactForm(formData);
      setSubmitted(true);
    } catch {
      // 即使 API 失败也显示成功给用户体验
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20office%20building%20exterior%20with%20glass%20facade%20blue%20sky%20professional%20corporate%20headquarters%20clean%20architecture%20professional%20photography%20high%20quality%20business%20environment&width=1400&height=800&seq=50&orientation=landscape"
              alt="Huatera office"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/60 to-dark/30"></div>
          </div>
          <div className="relative z-10 h-full flex flex-col justify-center px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-1.5 bg-white/20 rounded-full mb-6">
                <span className="text-white text-sm font-medium">Contact Us</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Let&apos;s Start a<br />
                <span className="text-secondary">Conversation</span>
              </h1>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6">
                  Get in Touch
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Have questions about our products or need a custom solution?
                  Our team at Quanzhou Shijia Rongyan Electronic Technology Co, Ltd.
                  is ready to help you find the perfect communication setup for your needs.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-dark font-semibold mb-1">Mobile Phone</h3>
                      <a href="tel:+8613788855889" className="text-gray-600 text-sm hover:text-primary transition-colors block">+86 137 8885 5889</a>
                      <a href={`tel:${(info?.company_phone || "+86 159 9998 9668").replace(/\s/g, '')}`} className="text-gray-600 text-sm hover:text-primary transition-colors block">{info?.company_phone || "+86 159 9998 9668"}</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-dark font-semibold mb-1">Email</h3>
                      <a href={`mailto:${info?.company_email || "huatera88@gmail.com"}`} className="text-gray-600 text-sm hover:text-primary transition-colors">{info?.company_email || "huatera88@gmail.com"}</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-dark font-semibold mb-1">Address</h3>
                      <p className="text-gray-600 text-sm whitespace-pre-line">{info?.company_address || "Gangwu (Chenghui International)\nQiuzhong Village, Xiamei Town\nNan'an City, Fujian Province"}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-dark font-semibold mb-1">Website</h3>
                      <a href="https://www.huatera.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 text-sm hover:text-primary transition-colors">www.huatera.com</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-building-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-dark font-semibold mb-1">Company</h3>
                      <p className="text-gray-600 text-sm">{info?.company_name || "Quanzhou Shijia Rongyan Electronic Technology Co, Ltd."}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-light-gray rounded-2xl p-6 md:p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-dark mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-gray-600">
                      Thank you for reaching out. We will get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-dark mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none transition-colors"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none transition-colors"
                        placeholder="john@company.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none transition-colors"
                        placeholder="+86 138 0000 0000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none transition-colors"
                        placeholder="Your company name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark mb-2">
                        Message
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none transition-colors resize-none"
                        placeholder="How can we help you?"
                      ></textarea>
                      <p className="text-xs text-gray-400 mt-1 text-right">
                        {formData.message.length}/500
                      </p>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-60"
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* QR Codes Section */}
        <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-light-gray">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
                Connect Instantly
              </h2>
              <p className="text-gray-600 max-w-xl mx-auto">
                Scan the QR code to reach us directly on your preferred messaging platform
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
              {chatChannels.map((channel) => (
                <div
                  key={channel.id}
                  className="bg-white rounded-2xl p-6 md:p-8 flex flex-col items-center text-center transition-transform hover:-translate-y-1"
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center text-white mb-5"
                    style={{ backgroundColor: channel.color }}
                  >
                    <i className={`${channel.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-2">
                    {channel.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-5">{channel.label}</p>

                  <div className="w-[160px] h-[160px] md:w-[180px] md:h-[180px] rounded-xl overflow-hidden bg-gray-50 border border-gray-100 mb-4">
                    <img
                      src={channel.qrCode}
                      alt={`${channel.name} QR code`}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <button
                    onClick={() => setActiveQr(channel)}
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-dark transition-colors"
                  >
                    <i className="ri-zoom-in-line"></i>
                    Enlarge QR Code
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* QR Code Modal */}
      {activeQr && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setActiveQr(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 md:p-8 mx-4 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                  style={{ backgroundColor: activeQr.color }}
                >
                  <i className={`${activeQr.icon} text-lg`}></i>
                </div>
                <h3 className="text-lg font-bold text-dark">{activeQr.name}</h3>
              </div>
              <button
                onClick={() => setActiveQr(null)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                aria-label="Close"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="flex flex-col items-center gap-4">
              <div className="w-[220px] h-[220px] md:w-[260px] md:h-[260px] rounded-xl overflow-hidden bg-gray-50 border border-gray-100">
                <img
                  src={activeQr.qrCode}
                  alt={`${activeQr.name} QR code`}
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-sm text-gray-500 text-center">{activeQr.label}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}